import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type ProfileStatus, type User } from "./api";
import { signOutUser, watchAuth, type FirebaseUser } from "./firebase";
import { analytics, AnalyticsEvent } from "./analytics";

/**
 * Three states, not two:
 *   signed out            — no Firebase user
 *   needs a username      — Firebase user, no profile row on our side yet
 *   ready                 — both
 *
 * Collapsing the middle state into "signed out" would bounce someone straight back to the
 * sign-in screen after a successful Google login, which looks like a broken app.
 */
interface AuthValue {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  needsProfile: boolean;
  profileStatus: ProfileStatus | null;
  isLoading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    return watchAuth((user) => {
      setFirebaseUser(user);
      setAuthResolved(true);
      // A different account must not see the previous one's cached data.
      queryClient.removeQueries();
    });
  }, [queryClient]);

  const { data, isLoading } = useQuery({
    queryKey: ["me", firebaseUser?.uid ?? null],
    enabled: authResolved && firebaseUser != null,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<{ user: User | null; status: ProfileStatus | null }> => {
      try {
        return { user: await api.auth.me(), status: null };
      } catch (error) {
        // 403 profile_required is expected on first sign-in, not a failure.
        if (error instanceof ApiError && error.code === "profile_required") {
          return { user: null, status: await api.auth.profileStatus() };
        }
        if (error instanceof ApiError && error.status === 401) {
          return { user: null, status: null };
        }
        throw error;
      }
    },
  });

  // Associates every subsequent event with this user (by internal id, never email or display
  // name — see the identify() doc on the GA provider for why) so behaviour can be traced across
  // a session without attaching anything personally identifying.
  useEffect(() => {
    if (data?.user) analytics.identify(data.user.id, { isSuperAdmin: data.user.isSuperAdmin });
  }, [data?.user]);

  const value = useMemo<AuthValue>(
    () => ({
      user: data?.user ?? null,
      firebaseUser,
      needsProfile: Boolean(firebaseUser && data && !data.user),
      profileStatus: data?.status ?? null,
      isLoading: !authResolved || (firebaseUser != null && isLoading),
      logout: async () => {
        analytics.track(AnalyticsEvent.SignOut);
        analytics.reset();
        await signOutUser();
        queryClient.clear();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      },
    }),
    [data, firebaseUser, authResolved, isLoading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
